let cloneDeep = require("lodash/cloneDeep");

import * as angular from "angular";
import {CurrentUser} from "../models/user/user.model";
import {UserService} from "../common/user/user.service";
import {Group} from "../models/groups/group.model";
import {Student} from "../models/student.model";
import {GroupsService} from "../common/groups/groups.service";
import {Link} from "../models/link.model";
import {MessagingService, MessageObject} from "../common/messaging/messaging.service";
import {UserSessionService} from "../common/user-session/user-session.service";
import {StudyProgram} from "../models/study-program.model";
import {Instructor} from "../models/instructor.model";
import {TypeaheadProviderService} from "../common/typeahead-provider/typeahead-provider.service";


type SortField = "name" | "classYear";
type typeaheadSource = Group | Instructor | StudyProgram;

const ALLOWED_PERMISSION_TO_EDIT_STUDENTS = "create_student_groups";
const EDIT_STUDENTS_URL = "/studentsmain/groups/groups.php?viewGroup=1&gid=";
const STUDENTS_PER_PAGE = 10;

const GROUP_INIT_DATA = "group";
const INSTRUCTORS_INIT_DATA = "instructors";
const STUDY_PROGRAMS_INIT_DATA = "studyPrograms";

enum Editor {instructors, study_programs}

export class GroupDetailController {
    crumbs: Link[];
    currentUser: CurrentUser;
    group: Group;
    tenantId: string;
    groupId: string;
    groupName: string;
    pages: Student[][];
    currentPage: number = 1;
    messages: MessageObject;
    editor: Editor;
    lastEdit: Editor;
    editedGroup: Group;
    pendingInstructorsUpdate: boolean = false;
    pendingStudyProgramsUpdate: boolean = false;
    instructors: Instructor[];
    studyPrograms: StudyProgram[];
    private editStudentsHost: string;
    isAscendingSort: boolean;
    sortIcon: string;
    isDistrict: boolean;
    showError: boolean = false;
    tooltipIsVisible: boolean = false;
    instructorTypeaheadPaths: string[] = [
        "name.firstName",
        "name.lastName",
        "name.displayName"
    ];
    programTypeaheadPaths: string[] = [
        "courseSlug",
        "courseTitle"
    ];
    sortField: SortField;
    pendingUpdate: Editor;
    tagType: typeaheadSource[];
    

    /* @ngInject */
    constructor(public $route: angular.route.IRouteService, userService: UserService, public $window: angular.IWindowService,
        SUCCEED: string, public groupsService: GroupsService, public messagingService: MessagingService,
        private userSessionService: UserSessionService,   private typeaheadProviderService: TypeaheadProviderService) {
        this.editStudentsHost = SUCCEED;
        this.isAscendingSort = false;
        this.sortIcon = "ns-icon--sort";
        this.groupId = this.$route.current.params.groupId;
        this.tenantId = this.$route.current.params.tenantId;
        userService.get().then(this.loadUser);
        this.messages = messagingService("students");
    }

    $onInit() {
        this.group = this.$route.current.locals[GROUP_INIT_DATA];

        this.instructors = this.$route.current.locals[INSTRUCTORS_INIT_DATA];
        if(this.instructors) {
            this.instructors.sort(GroupDetailController.sortInstructors);
        }

        this.studyPrograms = this.$route.current.locals[STUDY_PROGRAMS_INIT_DATA];
        if(this.studyPrograms) {
            this.studyPrograms.sort(GroupDetailController.sortStudyPrograms);
        }
        
        if (this.hasStudentsToShow()) {
            this.sortStudents("name");
            this.refreshDisplayedStudents();
        }
        this.updateCrumbs();
    }

    hideError(): void { this.showError = false; }

    getDisplayInfo(): string {
        let count = this.currentPage * STUDENTS_PER_PAGE;
        let start = count - (STUDENTS_PER_PAGE -1);
        let end = (count > this.getStudentCount()) ? this.getStudentCount() : count;
        return `${start}-${end}`;
    }

    setEditor(editor: string): void {
        this.lastEdit = void 0;
        this.editor = Editor[editor];
        this.editedGroup = cloneDeep(this.group);
    }

    isEditor(editor: string): boolean { return this.editor === Editor[editor]; }

    wasLastEdited(editor: string): boolean { return this.lastEdit === Editor[editor]; }

    isPendingUpdate(editor: string): boolean { return this.pendingUpdate === Editor[editor]; }

    closeEdit = (): void => {
        this.editor = void 0;
    };

    saveInstructorsEdit(): void {
        let closeEdit = true;
        if(!this.editedGroup || !this.isEditor(Editor[Editor.instructors])) {
            this.showError = true;
        } else {
            if (this.editedGroup.instructors.length > 0) {
                this.pendingUpdate = Editor.instructors;
                this.groupsService.updateInstructors(this.tenantId, this.editedGroup)
                    .then(this.handleGroupInstructorsUpdate);
            } else {
                closeEdit = false;
            }
        }

        if (closeEdit) {
            this.closeEdit();
        }
    }

    saveStudyProgramsEdit(): void {
        let closeEdit = true;
        if(!this.editedGroup || !this.isEditor(Editor[Editor.study_programs])) {
            this.showError = true;
        } else {
            if (this.editedGroup.productGroups.TESTPREP.studyPrograms.length > 0) {
                this.pendingUpdate = Editor.study_programs;
                this.groupsService.updateStudyPrograms(this.tenantId, this.editedGroup)
                    .then(this.handleGroupStudyProgramsUpdate);
            } else {
                closeEdit = false;
            }
        }

        if (closeEdit) {
            this.closeEdit();
        }
    }

    showPaginator(): boolean { return this.pages && this.pages.length > 1; }

    refreshDisplayedStudents(): void {
        this.pages = [[]];
        this.group.students.forEach((student: Student) => {
            let lastPage = this.pages[this.pages.length - 1];
            if (lastPage.length >= STUDENTS_PER_PAGE) {
                this.pages.push([]);
                lastPage = this.pages[this.pages.length - 1];
            }

            lastPage.push(student);
        });
        this.currentPage = Math.min(this.pages.length, this.currentPage);
    }

    getStudentCount(): number {
        return this.group.students && this.group.students.length;
    }

    getPageOfStudents(): Student[] {
        return this.pages && this.pages[this.currentPage - 1];
    }

    hasStudentsToShow(): boolean {
        return this.group && this.group.students && this.group.students.length > 0;
    }

    sortStudents(sortField: SortField):void {
        this.isAscendingSort = !this.isAscendingSort;
        if (sortField !== this.sortField) { this.isAscendingSort = true; }
        this.sortField = sortField;
        this.sortIcon = "ns-icon--sort-" + (this.isAscendingSort ? "up" : "down");
        if (sortField === "name") {
            this.group.students.sort(this.sortByStudentName);
        } else {
            this.group.students.sort(this.sortByClassYear);
        }
        this.refreshDisplayedStudents();
    }

    getSortIcon(sortField: SortField): string {
        if (this.sortField === sortField) { return this.sortIcon; }
        return "ns-icon--sort";
    }

    getMessage(): string {
        let key = "noStudents";
        return this.messages[key];
    }

    showEditStudentsButton(): boolean {
        if (this.currentUser && this.currentUser.permissions && this.currentUser.permissions.length > 0) {
            return this.currentUser.hasPermission(ALLOWED_PERMISSION_TO_EDIT_STUDENTS);
        }
    }

    showTooltip(): void { this.tooltipIsVisible = true; }
    hideTooltip(): void { this.tooltipIsVisible = false; }

    jumpToEditStudents(): void {
        this.$window.location.href = this.editStudentsHost + EDIT_STUDENTS_URL + this.group.groupId;
    }

    getGrade(student: Student): String {
        if(!student.grade) {
            return "";
        }

        if(student.grade > 12) {
            return "(ALUMNI)";
        }

        return "(Grade " + student.grade + ")";
    }

    private updateCrumbs() {
        if (this.userSessionService.isDistrictMode()) {
            this.crumbs = [
                {
                    name: "Naviance Test Prep: District",
                    url: `${this.userSessionService.getDistrictTenantId()}`
                },
                {
                    name: `${this.userSessionService.getCurrentSchool().name}`,
                    url: `${this.userSessionService.getDistrictTenantId()}`
                },
                { name: this.group.groupName }
            ];
        } else {
            this.crumbs = [
                {
                    name: "Naviance Test Prep",
                    url: `${this.tenantId}`
                },
                { name: this.group.groupName }
            ];
        }
    }
    handleAdd = (tag: any, type: string): void => {
      this.tagType = this.typeaheadProviderService.setTagType(type, this.editedGroup);
      this.tagType.push(tag);
    };
    handleRemove = (tag: any, type: string): void => {
        this.tagType = this.typeaheadProviderService.setTagType(type, this.editedGroup);
        this.tagType.splice(this.tagType.indexOf(tag), 1);
    };
    typeaheadProvider = (input: string, typeaheadSource: any, tags: string[], typeaheadPaths: string[]): any => {
        return this.typeaheadProviderService.typeaheadProvider(input, typeaheadSource, tags, typeaheadPaths);
    };

     hasNoInstructors(): boolean {
        return !this.editedGroup.instructors.length;
    }

    hasNoStudyPrograms(): boolean {
        return !this.editedGroup.productGroups.TESTPREP.studyPrograms.length;
    }

    private sortByStudentName = (a: Student, b: Student) => {
        let order = this.isAscendingSort ? 1 : -1;
        let lastNameSort = order * (a.lastName > b.lastName ? 1 : (b.lastName > a.lastName ? -1 : 0));
        if (!lastNameSort) {
            return order * (a.firstName > b.firstName ? 1 : (b.firstName > a.firstName ? -1 : 0));
        }
        return lastNameSort;
    };

    private sortByClassYear = (a: Student, b: Student) => {
        let order = this.isAscendingSort ? 1 : -1;
        return order * (a.classYear > b.classYear ? 1 : (b.classYear > a.classYear ? -1 : 0));
    };

    private loadUser = (currentUser: CurrentUser) => {
        this.currentUser = currentUser;
        this.updateCrumbs();
    };

    private handleGroupInstructorsUpdate = (status: number) => {
        if (!this.editedGroup || !status || status >= 400) {
            this.showError = true;
        } else {
            this.group.instructors = this.editedGroup.instructors;
            this.lastEdit = Editor.instructors;
        }

        this.editedGroup = undefined;
        this.pendingUpdate = void 0;
    };

    private handleGroupStudyProgramsUpdate = (status: number) => {
        if (!this.editedGroup || !status || status >= 400) {
            this.showError = true;
        } else {
            this.group.productGroups.TESTPREP.studyPrograms = this.editedGroup
                .productGroups.TESTPREP.studyPrograms;
            this.lastEdit = Editor.study_programs;
        }

        this.editedGroup = undefined;
        this.pendingUpdate = void 0;
    };

    private static sortInstructors(instructorA: Instructor, instructorB: Instructor): number {
        return instructorA.name.displayName.localeCompare(instructorB.name.displayName);
    }

    private static sortStudyPrograms(studyProgramA: StudyProgram, studyProgramB: StudyProgram): number {
        return studyProgramA.courseTitle.localeCompare(studyProgramB.courseTitle);
    }
}



/** WEBPACK FOOTER **
 ** ./~/tslint-loader!./app/group-detail/group-detail.controller.ts
 **/